import { useCallback, useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/Shared";

const ST = {
  success: ["Berhasil", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  partial: ["Sebagian", "bg-amber-50 text-amber-700 border-amber-200"],
  failed: ["Gagal", "bg-rose-50 text-rose-700 border-rose-200"],
  running: ["Berjalan", "bg-sky-50 text-sky-700 border-sky-200"],
};
const Pill = ({ s }) => <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${ST[s]?.[1]}`}>{ST[s]?.[0] || s}</span>;

function ConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/sync/config").then((r) => { setCfg(r.data); setUrl(r.data.sheet_url); }); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/sync/config", { sheet_url: url });
      setCfg(data);
      toast.success("Sumber spreadsheet disimpan. Tekan Refresh Sekarang untuk sinkron.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <h3 className="font-heading text-lg font-semibold">Konfigurasi Sumber</h3>
      <p className="text-xs text-muted-foreground">{cfg?.schedule} · spreadsheet harus dibagikan "Siapa saja yang memiliki link → Penampil". Aplikasi hanya membaca.</p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Input data-testid="sync-sheet-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" />
        <Button data-testid="sync-config-save-button" onClick={save} disabled={busy}>{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Simpan</Button>
      </div>
    </div>
  );
}

const EmptySummary = ({ items }) => (
  <div data-testid="empty-summary" className={`rounded-lg border p-3 text-xs ${items.length ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
    <p className="font-semibold">{items.length ? `Data kosong perlu dilengkapi di sheet (${items.length})` : "Tidak ada UP3 / UID / Kategori yang kosong"}</p>
    {items.length > 0 && (
      <ul className="mt-1 list-disc space-y-0.5 pl-4">
        {items.map((i) => <li key={`${i.sheet}-${i.column}`} data-testid={`empty-summary-${i.sheet}-${i.column}`}><b>{i.sheet}</b> · {i.message.replace("Ringkasan data kosong: ", "")}</li>)}
      </ul>
    )}
  </div>
);

function IssuesDialog({ id, onClose }) {
  const [log, setLog] = useState(null);
  const [sev, setSev] = useState("all");
  useEffect(() => { if (id) api.get(`/sync/logs/${id}`).then((r) => setLog(r.data)); else setLog(null); }, [id]);
  const issues = (log?.issues || []).filter((i) => sev === "all" || i.severity === sev);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Detail Validasi Sync</DialogTitle></DialogHeader>
        {!log ? <Loader2 className="mx-auto h-6 w-6 animate-spin" /> : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(log.counts || {}).map(([k, v]) => <span key={k} className="rounded border bg-muted px-2 py-1">{k}: <b className="font-num">{v}</b></span>)}
            </div>
            {log.message && <p className="rounded bg-rose-50 p-2 text-sm text-rose-700">{log.message}</p>}
            <EmptySummary items={log.issues.filter((i) => i.summary)} />
            <Tabs value={sev} onValueChange={setSev}>
              <TabsList>
                <TabsTrigger data-testid="issues-tab-all" value="all">Semua ({log.issues.length})</TabsTrigger>
                <TabsTrigger data-testid="issues-tab-error" value="error">Error ({log.errors})</TabsTrigger>
                <TabsTrigger data-testid="issues-tab-warning" value="warning">Peringatan ({log.warnings})</TabsTrigger>
              </TabsList>
            </Tabs>
            <div data-testid="issues-list" className="max-h-[50vh] overflow-auto rounded border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted"><tr><th className="p-2 text-left">Sheet</th><th className="p-2 text-left">Baris</th><th className="p-2 text-left">Kolom</th><th className="p-2 text-left">Pesan</th></tr></thead>
                <tbody>
                  {issues.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Tidak ada temuan.</td></tr>}
                  {issues.map((i, n) => (
                    <tr key={n} className="border-t">
                      <td className="p-2 font-medium">{i.sheet}</td><td className="p-2 font-num">{i.row ?? "–"}</td><td className="p-2">{i.column ?? "–"}</td>
                      <td className={`p-2 ${i.severity === "error" ? "text-rose-700" : "text-amber-700"}`}>{i.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SyncPage() {
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/sync/logs").then((r) => setLogs(r.data)), []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader eyebrow="Administrasi" title="Sinkronisasi Google Sheets" desc="Setiap sync menyimpan snapshot ke database. Kolom hilang, nilai kosong, dan tipe data salah dicatat — tidak diam-diam dilewati." />
      <ConfigCard />
      <div className="rounded-xl border bg-card shadow-sm">
        <div className="border-b p-4"><h3 className="font-heading text-lg font-semibold">Riwayat Sync</h3></div>
        <div className="overflow-x-auto">
          <table data-testid="sync-logs-table" className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr><th className="p-3 text-left">Waktu</th><th className="p-3 text-left">Pemicu</th><th className="p-3 text-left">Status</th><th className="p-3 text-right">Baris</th><th className="p-3 text-right">Error</th><th className="p-3 text-right">Peringatan</th><th className="p-3" /></tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={l.id} className="border-t">
                  <td className="p-3 whitespace-nowrap">{fmtDate(l.started_at)}</td>
                  <td className="p-3 capitalize">{l.trigger}{l.by ? ` · ${l.by}` : ""}</td>
                  <td className="p-3"><Pill s={l.status} /></td>
                  <td className="p-3 text-right font-num">{l.total_records}</td>
                  <td className="p-3 text-right font-num text-rose-600">{l.errors}</td>
                  <td className="p-3 text-right font-num text-amber-600">{l.warnings}</td>
                  <td className="p-3 text-right"><Button data-testid={`sync-log-detail-${i}`} size="sm" variant="outline" onClick={() => setOpen(l.id)}>Detail</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <IssuesDialog id={open} onClose={() => setOpen(null)} />
    </div>
  );
}
